/**
 * 공용 문의 서버(`admin-support`) API 계약.
 *
 * **`server/support` 의 Kotlin DTO 와 필드 이름·타입이 1:1이다** — 한쪽만 고치면
 * 앱이 조용히 undefined 를 읽거나 어드민 화면이 빈 칸을 그린다. 같은 커밋에서 함께 고칠 것.
 * 서비스별 계약(`frontend/<svc>/contract`)과 달리 이건 전 앱이 공유하므로 shared 에 있다.
 */

export type SupportPlatform = 'ANDROID' | 'IOS' | 'WEB' | 'DESKTOP'

/** 운영자가 먼저 훑는 축. 늘리면 서버 enum·어드민 필터를 같은 커밋에서 함께 고친다 */
export type SupportCategory = 'BUG' | 'IDEA' | 'ACCOUNT' | 'ADS' | 'ETC'

/**
 * 처리 상태. **`REPLY_PENDING` 은 "답을 다 썼고 메일만 안 나갔다"는 뜻이다**
 * (2026-08-24 사용자 지시: "문의사항을 해결한 뒤, 메일전송 대기 상태도 만들어서").
 * 보내고 나면 서버가 `DONE` 으로 옮긴다 — 사람이 상태를 다시 고를 필요가 없다.
 */
export type SupportStatus = 'NEW' | 'IN_PROGRESS' | 'REPLY_PENDING' | 'DONE'

/**
 * 앱이 보내는 문의. 환경 정보는 사용자에게 묻지 않고 앱이 채운다.
 *
 * **회신 주소는 선택이다 (2026-08-24 사용자 지시: "지원받을 메일주소(선택사항)도 넣게
 * 해줘").** 2026-08-12 에는 반대였고("이메일 받지마"), 그때 계약·DB 컬럼·화면·방침에서
 * 자리를 통째로 걷어냈다 — 되돌리는 지금도 같은 넷을 함께 되돌린다. 한 곳만 되살리면
 * 화면은 주소를 받는데 서버가 버리거나, 서버는 저장하는데 방침이 "받지 않는다"고 적힌
 * 상태가 된다 (docs/support.md·docs/privacy-policy.md).
 *
 * **사진은 이 객체에 없다.** 붙이는 순간 요청이 multipart 가 되고, 이 JSON 은 `inquiry`
 * 파트로, 사진은 `photos` 파트로 나간다 — base64 로 여기 실으면 본문이 1.4배로 부풀고
 * 서버가 그 문자열 전체를 메모리에 올린다 (`support-native` 의 `client.ts`).
 */
export interface SubmitInquiryRequest {
  /** 보낸 앱의 서비스명 (폴더·컨테이너와 같은 값) */
  service: string
  platform: SupportPlatform
  category: SupportCategory
  message: string
  /**
   * 답장받을 주소. **비워도 문의는 접수된다** — 로그인이 없는 앱도 같은 화면을 쓰고,
   * 답을 원하지 않는 사람에게 주소를 강요할 이유가 없다.
   */
  contactEmail?: string
  appVersion?: string
  osVersion?: string
  deviceModel?: string
  locale?: string
}

/** 접수 번호만 돌려준다 — 사용자가 나중에 댈 수 있는 값 하나면 충분하다 */
export interface SubmitInquiryResponse {
  id: string
  /** ISO-8601 */
  createdAt: string
}

/**
 * 문의에 붙는 사진 한 장.
 *
 * **바이트가 아니라 메타데이터다.** 목록·상세 응답에 이미지를 실어 보내면 어드민 목록
 * 한 페이지가 수십 MB 가 된다 — 실제 이미지는 어드민 전용 경로
 * (`/api/support/admin/inquiries/{id}/photos/{photoId}`)로 한 장씩 내려간다.
 */
export interface InquiryPhoto {
  id: string
  /** `SUPPORT_PHOTO_MEDIA_TYPES` 중 하나 */
  mediaType: string
  byteSize: number
}

/** 어드민 목록 행 — 본문은 미리보기만 온다 */
export interface InquirySummary {
  id: string
  service: string
  platform: SupportPlatform
  category: SupportCategory
  status: SupportStatus
  preview: string
  /** 붙은 사진 수. 목록에서 "사진 있음"을 표시하는 데만 쓴다 */
  photoCount: number
  createdAt: string
}

export interface InquiryListResponse {
  items: InquirySummary[]
  /** 다음 페이지 커서. null 이면 끝이다 */
  nextBefore: string | null
  /** 상태별 전체 건수 — 사이드바 배지 */
  counts: Partial<Record<SupportStatus, number>>
}

export interface InquiryDetail {
  id: string
  service: string
  platform: SupportPlatform
  category: SupportCategory
  status: SupportStatus
  message: string
  /** 답장받을 주소. 안 적었으면 null 이고, 어드민은 그때 "답장 불가"로 읽는다 */
  contactEmail: string | null
  /** 운영자가 쓴 답변. 보내기 전에도 초안으로 저장된다 */
  replyBody: string | null
  /** 답장 메일이 실제로 나간 시각(ISO-8601). null 이면 아직 안 나갔다 */
  replySentAt: string | null
  appVersion: string | null
  osVersion: string | null
  deviceModel: string | null
  locale: string | null
  /** 사용자가 붙인 사진. 없으면 빈 배열이다 */
  photos: InquiryPhoto[]
  adminNote: string | null
  createdAt: string
  updatedAt: string
}

/**
 * 상태·메모·답변 초안을 고친다. **문의 본문은 사용자가 쓴 것이라 운영자가 고칠 수 없다.**
 *
 * `adminNote` 와 `replyBody` 는 자리가 다르다 — 메모는 우리끼리 보는 처리 기록이고,
 * 답변은 **그대로 이용자에게 메일로 나간다.** 한 칸에 섞으면 내부 메모가 메일에 실린다.
 */
export interface UpdateInquiryRequest {
  status: SupportStatus
  adminNote?: string | null
  replyBody?: string | null
}

/** 답장 보내기 — 지금 화면에 있는 답변을 저장하고 그대로 발송한다 */
export interface SendReplyRequest {
  body: string
}

/** 답변 길이. 메일 한 통에 들어갈 만큼이면 충분하다 */
export const REPLY_MAX_LENGTH = 4000

/** 답장을 보내는 주소 — 발신 전용이다 (수신함이 없다) */
export const REPLY_FROM_ADDRESS = 'no-reply@cottonray.com'

/**
 * **앱이 여는 문의 화면.** 2026-08-26 사용자 지시로 문의가 앱 안 네이티브 화면에서 이
 * 웹페이지 하나로 옮겨 왔다 ("문의하기를 웹사이트로 배포하고. cottonray.com 에 하나로").
 *
 * 어느 앱의 문의인지는 **경로가 아니라 `?service=`** 로 간다 — 화면은 하나고 서비스는
 * 값이다. 앱은 자기 테마·환경도 함께 실어 보낸다 (`@cottonray/support-native` 의
 * `supportUrl()`, 받는 쪽은 `frontend/cottonray/web/src/support/params.ts`).
 */
export const SUPPORT_PAGE_URL = 'https://cottonray.com/support'

/**
 * 접수 경로. **문의 화면과 같은 출처라 상대 경로로 부른다** — 그래서 서버에 교차 출처
 * 설정이 필요 없다. nginx 는 이 경로만 루트 도메인에 POST 로 연다 (docs/support.md).
 */
export const SUPPORT_SUBMIT_PATH = '/api/support/inquiries'

/**
 * 접수 경로의 절대 주소. **서브도메인이 아니라 루트 도메인이다** — 문의 서버는
 * 내부 컴포넌트라 `support.cottonray.com` 를 만들지 않는다 (docs/support.md).
 * 문서의 `curl` 한 줄과 앱 밖에서 부르는 도구가 이 값을 쓴다.
 */
export const SUPPORT_SUBMIT_URL = `https://cottonray.com${SUPPORT_SUBMIT_PATH}`

/** 서버 검증(@Size)과 같은 값. 앱이 먼저 막아 사용자가 보내기 전에 알게 한다 */
export const MESSAGE_MIN_LENGTH = 5
export const MESSAGE_MAX_LENGTH = 4000

/** 회신 주소 길이 — RFC 5321 의 주소 최대 길이이자 DB 컬럼 폭이다 */
export const EMAIL_MAX_LENGTH = 254

/**
 * 사진 첨부 한도 (2026-08-24 사용자 지시: "모든 앱 지원·문의에 사진첨부기능 더해줘").
 *
 * **서버 검증·nginx 상한과 같은 값이어야 한다** — 앱이 먼저 막지 않으면 사용자는 다 쓴
 * 문의를 보내고 나서야 413 을 본다. 서버 쪽 짝은 `server/support` 의
 * `spring.servlet.multipart` 와 `SupportProperties.maxPhotos` 이고, 그 앞에 인프라 nginx 의
 * `client_max_body_size` 가 하나 더 있다 (docs/support.md §사진 첨부).
 */
export const MAX_PHOTOS = 3

/** 한 장의 상한. 앱이 고르는 단계에서 줄여 보내므로 평소엔 이 근처에도 안 간다 */
export const MAX_PHOTO_BYTES = 4 * 1024 * 1024

/**
 * 받는 형식. **서버에 이미지 라이브러리가 없어 재인코딩을 못 한다** — 받은 그대로
 * 보관하고 그대로 내려주므로, 어드민 브라우저가 열 수 있는 형식만 받는다
 * (`AvatarStorage` 와 같은 목록).
 */
export const SUPPORT_PHOTO_MEDIA_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const

/** 화면 문구의 단일 출처 — 앱과 어드민이 같은 말을 쓴다 */
export const CATEGORY_LABEL: Record<SupportCategory, string> = {
  BUG: '오류·버그',
  IDEA: '기능 제안',
  ACCOUNT: '계정·로그인',
  ADS: '광고',
  ETC: '기타',
}

export const STATUS_LABEL: Record<SupportStatus, string> = {
  NEW: '새 문의',
  IN_PROGRESS: '처리 중',
  REPLY_PENDING: '메일 전송 대기',
  DONE: '완료',
}

export const PLATFORM_LABEL: Record<SupportPlatform, string> = {
  ANDROID: 'Android',
  IOS: 'iOS',
  WEB: '웹',
  DESKTOP: '데스크톱',
}
